import {
  Component,
  lazy,
  Suspense,
  useEffect,
  useState,
  type ErrorInfo,
  type ReactNode,
} from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import {
  Link,
  Navigate,
  NavLink,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router';
import { z } from 'zod';
import { toast } from 'sonner';
import { AnimatePresence, motion } from 'framer-motion';
import {
  BadgePercent,
  Bell,
  Building2,
  CalendarDays,
  ChartNoAxesCombined,
  CircleDollarSign,
  ClipboardList,
  FileCheck2,
  Globe2,
  KeyRound,
  LayoutDashboard,
  Layers3,
  LogOut,
  Menu,
  Settings,
  ScrollText,
  Sparkles,
  TableProperties,
  Users,
  UtensilsCrossed,
  X,
} from 'lucide-react';
import { api, apiErrorMessage, setAccessToken } from './api';

const AreasPage = lazy(() =>
  import('./operational-pages').then((module) => ({ default: module.AreasPage })),
);
const CustomersPage = lazy(() =>
  import('./operational-pages').then((module) => ({ default: module.CustomersPage })),
);
const DashboardPage = lazy(() =>
  import('./operational-pages').then((module) => ({ default: module.DashboardPage })),
);
const EventsPage = lazy(() =>
  import('./operational-pages').then((module) => ({ default: module.EventsPage })),
);
const PaymentSettingsPage = lazy(() =>
  import('./operational-pages').then((module) => ({ default: module.PaymentSettingsPage })),
);
const ReportsPage = lazy(() =>
  import('./operational-pages').then((module) => ({ default: module.ReportsPage })),
);
const ReservationsPage = lazy(() =>
  import('./operational-pages').then((module) => ({ default: module.ReservationsPage })),
);
const UsersPage = lazy(() =>
  import('./operational-pages').then((module) => ({ default: module.UsersPage })),
);
const WaitlistPage = lazy(() =>
  import('./operational-pages').then((module) => ({ default: module.WaitlistPage })),
);
const SettingsPage = lazy(() =>
  import('./settings-page').then((module) => ({ default: module.SettingsPage })),
);
const NotificationsPage = lazy(() =>
  import('./notifications-page').then((module) => ({ default: module.NotificationsPage })),
);
const OnboardingPage = lazy(() =>
  import('./onboarding-page').then((module) => ({ default: module.OnboardingPage })),
);
const RegisterPage = lazy(() =>
  import('./registration-pages').then((module) => ({ default: module.RegisterPage })),
);
const VerifyEmailPage = lazy(() =>
  import('./registration-pages').then((module) => ({ default: module.VerifyEmailPage })),
);
const TenantSetupPage = lazy(() =>
  import('./registration-pages').then((module) => ({ default: module.TenantSetupPage })),
);
const ForgotPasswordPage = lazy(() =>
  import('./registration-pages').then((module) => ({ default: module.ForgotPasswordPage })),
);
const ResetPasswordPage = lazy(() =>
  import('./registration-pages').then((module) => ({ default: module.ResetPasswordPage })),
);
const InvitationPage = lazy(() =>
  import('./invitation-page').then((module) => ({ default: module.InvitationPage })),
);
const SuperAdminAuditPage = lazy(() =>
  import('./super-admin-pages').then((module) => ({ default: module.SuperAdminAuditPage })),
);
const SuperAdminCouponsPage = lazy(() =>
  import('./super-admin-pages').then((module) => ({ default: module.SuperAdminCouponsPage })),
);
const SuperAdminDashboardPage = lazy(() =>
  import('./super-admin-pages').then((module) => ({ default: module.SuperAdminDashboardPage })),
);
const SuperAdminPlansPage = lazy(() =>
  import('./super-admin-pages').then((module) => ({ default: module.SuperAdminPlansPage })),
);
const SuperAdminTenantsPage = lazy(() =>
  import('./super-admin-pages').then((module) => ({ default: module.SuperAdminTenantsPage })),
);
const SuperAdminBillingPage = lazy(() =>
  import('./super-admin-advanced-pages').then((module) => ({
    default: module.SuperAdminBillingPage,
  })),
);
const SuperAdminDomainsPage = lazy(() =>
  import('./super-admin-advanced-pages').then((module) => ({
    default: module.SuperAdminDomainsPage,
  })),
);
const SuperAdminEntitlementsPage = lazy(() =>
  import('./super-admin-advanced-pages').then((module) => ({
    default: module.SuperAdminEntitlementsPage,
  })),
);
const SuperAdminLegalPage = lazy(() =>
  import('./super-admin-advanced-pages').then((module) => ({
    default: module.SuperAdminLegalPage,
  })),
);
const SuperAdminUsersPage = lazy(() =>
  import('./super-admin-advanced-pages').then((module) => ({
    default: module.SuperAdminUsersPage,
  })),
);

type Session = {
  user: { id: string; name: string; email: string; isSuperAdmin: boolean };
  tenant: { slug: string; status: string } | null;
};
type LoginResponse =
  | {
      accessToken: string;
      user: Session['user'];
      tenant: Session['tenant'];
      requiresTenantSelection?: false;
    }
  | { requiresTenantSelection: true; tenants: { slug: string }[] };
const loginFormSchema = z.object({
  email: z.string().email('Informe um e-mail válido'),
  password: z.string().min(1, 'Informe sua senha'),
  tenantSlug: z.string().optional(),
});
type LoginForm = z.infer<typeof loginFormSchema>;

export function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [restoring, setRestoring] = useState(true);
  useEffect(() => {
    api
      .post<{ accessToken: string; user: Session['user']; tenant: Session['tenant'] }>(
        '/auth/session',
      )
      .then(({ data }) => signIn({ user: data.user, tenant: data.tenant }, data.accessToken))
      .catch(() => undefined)
      .finally(() => setRestoring(false));
  }, []);
  const signIn = (value: Session, access: string) => {
    setSession(value);
    setToken(access);
    setAccessToken(access);
  };
  const signOut = async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      setSession(null);
      setToken(null);
      setAccessToken(null);
    }
  };
  const finishTenantSetup = (tenant: { slug: string; status: string }, access: string) => {
    setSession((current) => (current ? { ...current, tenant } : current));
    setToken(access);
    setAccessToken(access);
  };
  if (restoring)
    return (
      <main className="min-h-screen grid place-items-center bg-[#f5f2eb] dark:bg-[#0e2421]">
        <p className="text-sm text-[#586a66] dark:text-white/60">Restaurando sessão segura…</p>
      </main>
    );
  return (
    <AppErrorBoundary>
      <Routes>
        <Route
          path="/criar-conta"
          element={session ? <Navigate to="/" replace /> : <RegisterPage />}
        />
        <Route
          path="/confirmar-email"
          element={session ? <Navigate to="/" replace /> : <VerifyEmailPage />}
        />
        <Route
          path="/entrar"
          element={session ? <Navigate to="/" replace /> : <Login onLogin={signIn} />}
        />
        <Route
          path="/esqueci-senha"
          element={session ? <Navigate to="/" replace /> : <ForgotPasswordPage />}
        />
        <Route
          path="/redefinir-senha"
          element={session ? <Navigate to="/" replace /> : <ResetPasswordPage />}
        />
        <Route
          path="/convite"
          element={
            session && token ? (
              <InvitationPage onAccepted={finishTenantSetup} />
            ) : (
              <InvitationSignInRedirect />
            )
          }
        />
        <Route
          path="/*"
          element={
            session && token ? (
              session.tenant ? (
                <Shell session={session} onLogout={signOut} />
              ) : (
                <TenantSetupPage userName={session.user.name} onCreated={finishTenantSetup} />
              )
            ) : (
              <Navigate to="/entrar" replace />
            )
          }
        />
      </Routes>
    </AppErrorBoundary>
  );
}

class AppErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(_error: Error, _info: ErrorInfo) {
    void _error;
    void _info;
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <main className="min-h-screen grid place-items-center bg-[#f5f2eb] p-6 text-[#173c36] dark:bg-[#0e2421] dark:text-white">
        <section role="alert" className="panel w-full max-w-xl p-8 text-center">
          <p className="text-sm font-bold uppercase tracking-[.16em] text-[#b43a2f]">
            Erro inesperado
          </p>
          <h1 className="mt-3 text-3xl font-semibold">Não foi possível exibir esta área</h1>
          <p className="mt-3 text-[#586a66] dark:text-white/65">
            Seus dados não foram alterados. Recarregue o painel e tente novamente.
          </p>
          <button
            className="focus-ring mt-6 rounded-xl bg-[#173c36] px-5 py-3 font-semibold text-white dark:bg-[#b43a2f]"
            onClick={() => window.location.reload()}
          >
            Recarregar painel
          </button>
        </section>
      </main>
    );
  }
}

function Login({ onLogin }: { onLogin: (session: Session, token: string) => void }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const invitationToken = params.get('convite');
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginForm>({ resolver: zodResolver(loginFormSchema) });
  const submit = async (values: LoginForm) => {
    try {
      const { data } = await api.post<LoginResponse>('/auth/login', values);
      if (data.requiresTenantSelection) {
        toast.info('Informe o identificador do restaurante para continuar.');
        return;
      }
      onLogin({ user: data.user, tenant: data.tenant }, data.accessToken);
      if (invitationToken)
        void navigate(`/convite?token=${encodeURIComponent(invitationToken)}`, { replace: true });
    } catch (error) {
      toast.error(apiErrorMessage(error), { duration: 8000 });
    }
  };
  return (
    <main className="min-h-screen grid lg:grid-cols-[1.08fr_.92fr] bg-[#f5f2eb] dark:bg-[#0e2421]">
      <section className="hidden lg:flex relative overflow-hidden bg-[#173c36] text-white p-16 flex-col justify-between">
        <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_20%_10%,#e9a23b,transparent_32%),radial-gradient(circle_at_80%_80%,#b43a2f,transparent_35%)]" />
        <Brand />
        <div className="relative max-w-xl">
          <p className="text-[#f3c978] font-semibold tracking-widest uppercase text-sm">
            Operação em sintonia
          </p>
          <h1 className="mt-4 text-6xl font-semibold leading-[1.05]">
            Cada mesa,
            <br />
            cada momento,
            <br />
            <span className="text-[#f3c978]">sob controle.</span>
          </h1>
          <p className="mt-7 text-lg text-white/70 max-w-md">
            Reservas, eventos, pagamentos e atendimento em um painel criado para o ritmo real do
            salão.
          </p>
        </div>
        <p className="relative text-sm text-white/55">restaurantehub.com.br</p>
      </section>
      <section className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-10">
            <Brand dark />
          </div>
          <p className="text-sm font-bold text-[#b43a2f] uppercase tracking-[.18em]">Área segura</p>
          <h2 className="mt-3 text-4xl font-semibold text-[#173c36] dark:text-white">
            Bem-vindo de volta
          </h2>
          <p className="mt-3 text-[#586a66] dark:text-white/60">
            Entre para acompanhar a operação do seu restaurante.
          </p>
          <form className="mt-9 space-y-5" onSubmit={handleSubmit(submit)}>
            <Field label="E-mail" error={errors.email?.message}>
              <input
                {...register('email')}
                autoComplete="email"
                className="focus-ring w-full rounded-xl border border-[#173c36]/15 bg-white/80 px-4 py-3 text-[#173c36]"
              />
            </Field>
            <Field label="Senha" error={errors.password?.message}>
              <input
                {...register('password')}
                type="password"
                autoComplete="current-password"
                className="focus-ring w-full rounded-xl border border-[#173c36]/15 bg-white/80 px-4 py-3 text-[#173c36]"
              />
            </Field>
            <Field label="Identificador do restaurante (se houver mais de um)">
              <input
                {...register('tenantSlug')}
                className="focus-ring w-full rounded-xl border border-[#173c36]/15 bg-white/80 px-4 py-3 text-[#173c36]"
                placeholder="ex.: bistro-aurora"
              />
            </Field>
            <button
              disabled={isSubmitting}
              className="focus-ring w-full rounded-xl bg-[#b43a2f] hover:bg-[#933027] disabled:opacity-60 text-white font-bold py-3.5 transition"
            >
              {isSubmitting ? 'Entrando…' : 'Entrar no painel'}
            </button>
          </form>
          <p className="mt-7 text-center text-sm text-[#586a66]">
            <Link className="font-bold text-[#b43a2f] underline" to="/esqueci-senha">
              Esqueci minha senha
            </Link>
          </p>
          <p className="mt-4 text-center text-sm text-[#586a66]">
            Ainda não possui conta?{' '}
            <Link
              className="font-bold text-[#b43a2f] underline"
              to={
                invitationToken
                  ? `/criar-conta?convite=${encodeURIComponent(invitationToken)}`
                  : '/criar-conta'
              }
            >
              Criar conta
            </Link>
          </p>
        </div>
      </section>
    </main>
  );
}

function InvitationSignInRedirect() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  return <Navigate to={`/entrar?convite=${encodeURIComponent(token)}`} replace />;
}

const restaurantNav = [
  ['/', 'Visão geral', LayoutDashboard],
  ['/reservas', 'Reservas', CalendarDays],
  ['/eventos', 'Eventos', Sparkles],
  ['/espera', 'Lista de espera', ClipboardList],
  ['/clientes', 'Clientes', Users],
  ['/equipe', 'Equipe e acessos', Users],
  ['/mesas', 'Áreas e mesas', TableProperties],
  ['/pagamentos', 'Pagamentos', CircleDollarSign],
  ['/notificacoes', 'Notificações', Bell],
  ['/relatorios', 'Relatórios', ChartNoAxesCombined],
  ['/configuracoes', 'Configurações', Settings],
] as const;
const adminNav = [
  ['/admin', 'Visão global', LayoutDashboard],
  ['/admin/restaurantes', 'Restaurantes', Building2],
  ['/admin/financeiro', 'Assinaturas', CircleDollarSign],
  ['/admin/liberacoes', 'Liberações', KeyRound],
  ['/admin/usuarios', 'Usuários globais', Users],
  ['/admin/dominios', 'Domínios', Globe2],
  ['/admin/planos', 'Planos e módulos', Layers3],
  ['/admin/cupons', 'Cupons', BadgePercent],
  ['/admin/documentos', 'Documentos legais', FileCheck2],
  ['/admin/auditoria', 'Auditoria', ScrollText],
] as const;
function Shell({ session, onLogout }: { session: Session; onLogout: () => void }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigation = session.user.isSuperAdmin
    ? adminNav
    : session.tenant?.status === 'ONBOARDING'
      ? ([['/onboarding', 'Primeiros passos', Sparkles], ...restaurantNav] as const)
      : restaurantNav;
  return (
    <div className="min-h-screen bg-[#f5f2eb] dark:bg-[#0e2421] text-[#173c36] dark:text-white">
      <aside
        className={`fixed z-40 inset-y-0 left-0 w-72 bg-[#173c36] text-white p-5 transition-transform lg:translate-x-0 ${open ? 'translate-x-0' : '-translate-x-full'}`}
      >
        <div className="flex items-center justify-between">
          <Brand />
          <button className="lg:hidden" onClick={() => setOpen(false)} aria-label="Fechar menu">
            <X />
          </button>
        </div>
        <nav className="mt-10 space-y-1" aria-label="Principal">
          {navigation.map(([href, label, Icon]) => (
            <NavLink
              key={href}
              to={href}
              end={href === '/' || href === '/admin'}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `focus-ring flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition ${isActive ? 'bg-white text-[#173c36]' : 'text-white/70 hover:bg-white/10 hover:text-white'}`
              }
            >
              <Icon size={19} />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="absolute bottom-5 inset-x-5 border-t border-white/10 pt-5">
          <p className="text-sm font-semibold truncate">{session.user.name}</p>
          <p className="text-xs text-white/65 truncate">{session.user.email}</p>
          <button
            onClick={onLogout}
            className="focus-ring mt-4 flex items-center gap-2 text-sm text-white/70 hover:text-white"
          >
            <LogOut size={17} />
            Sair
          </button>
        </div>
      </aside>
      <div className="lg:pl-72">
        <header className="h-20 px-5 sm:px-8 flex items-center gap-4 border-b border-[#173c36]/10 dark:border-white/10">
          <button className="lg:hidden" onClick={() => setOpen(true)} aria-label="Abrir menu">
            <Menu />
          </button>
          <div>
            <p className="text-xs uppercase tracking-wider text-[#52645f]">
              {session.tenant?.slug ?? 'Super Admin'}
            </p>
            <h1 className="font-semibold">
              {navigation.find(([href]) => href === location.pathname)?.[1] ?? 'RestauranteHub'}
            </h1>
          </div>
          <div className="ml-auto flex items-center gap-2 text-xs font-bold rounded-full bg-[#dff3e8] text-[#1b6847] px-3 py-1.5">
            <span className="h-2 w-2 rounded-full bg-[#2a9868]" />
            {session.tenant?.status ?? 'ADMIN'}
          </div>
        </header>
        <AnimatePresence mode="wait">
          <motion.main
            key={location.pathname}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="p-5 sm:p-8"
          >
            <Suspense fallback={<div className="panel p-8">Carregando área segura…</div>}>
              {session.user.isSuperAdmin ? (
                <Routes>
                  <Route index element={<Navigate to="/admin" replace />} />
                  <Route path="admin" element={<SuperAdminDashboardPage />} />
                  <Route path="admin/restaurantes" element={<SuperAdminTenantsPage />} />
                  <Route path="admin/financeiro" element={<SuperAdminBillingPage />} />
                  <Route path="admin/liberacoes" element={<SuperAdminEntitlementsPage />} />
                  <Route path="admin/usuarios" element={<SuperAdminUsersPage />} />
                  <Route path="admin/dominios" element={<SuperAdminDomainsPage />} />
                  <Route path="admin/planos" element={<SuperAdminPlansPage />} />
                  <Route path="admin/cupons" element={<SuperAdminCouponsPage />} />
                  <Route path="admin/documentos" element={<SuperAdminLegalPage />} />
                  <Route path="admin/auditoria" element={<SuperAdminAuditPage />} />
                  <Route path="*" element={<Navigate to="/admin" replace />} />
                </Routes>
              ) : (
                <Routes>
                  <Route
                    index
                    element={
                      session.tenant?.status === 'ONBOARDING' ? (
                        <Navigate to="/onboarding" replace />
                      ) : (
                        <DashboardPage />
                      )
                    }
                  />
                  <Route path="onboarding" element={<OnboardingPage />} />
                  <Route path="reservas" element={<ReservationsPage />} />
                  <Route path="eventos" element={<EventsPage />} />
                  <Route path="espera" element={<WaitlistPage />} />
                  <Route path="clientes" element={<CustomersPage />} />
                  <Route path="equipe" element={<UsersPage />} />
                  <Route path="mesas" element={<AreasPage />} />
                  <Route path="pagamentos" element={<PaymentSettingsPage />} />
                  <Route path="notificacoes" element={<NotificationsPage />} />
                  <Route path="relatorios" element={<ReportsPage />} />
                  <Route path="configuracoes" element={<SettingsPage />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              )}
            </Suspense>
          </motion.main>
        </AnimatePresence>
      </div>
    </div>
  );
}

function Brand({ dark = false }: { dark?: boolean }) {
  return (
    <div
      className={`relative flex items-center gap-3 font-bold text-lg ${dark ? 'text-[#173c36]' : ''}`}
    >
      <span className="grid place-items-center h-10 w-10 rounded-xl bg-[#b43a2f] text-white">
        <UtensilsCrossed size={22} />
      </span>
      <span>
        Restaurante<span className={dark ? 'text-[#b43a2f]' : 'text-[#f3c978]'}>Hub</span>
      </span>
    </div>
  );
}
function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string | undefined;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-sm font-semibold mb-2">{label}</span>
      {children}
      {error && (
        <span role="alert" className="block mt-1.5 text-sm text-[#b43a2f]">
          {error}
        </span>
      )}
    </label>
  );
}
